import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
} from "@mui/material";
import {
  SearchRounded,
  RefreshOutlined,
  EditOutlined,
  DeleteOutlineRounded,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import CompanyEditDialog from "../components/CompanyEditDialog";
import { textPrimary, textSecondary } from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PAGE_SIZE = 13;

const CONTRACT_CHIP = {
  Var: { fg: "#15803D", bg: "#DCFCE7", border: "#86EFAC" },
  Yok: { fg: "#B91C1C", bg: "#FEE2E2", border: "#FCA5A5" },
  Yeni: { fg: "#1D4ED8", bg: "#DBEAFE", border: "#93C5FD" },
};

const contractOf = (raw) => {
  if (raw === "yes") return "Var";
  if (raw === "no") return "Yok";
  if (raw === "new" || raw === "New") return "Yeni";
  return raw || "-";
};

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

export default function AdminCompanies() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [editDialog, setEditDialog] = useState({ open: false, company: null });
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  /* ─────────── Veri yükleme ─────────── */
  const loadData = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const data = await adminService.getCompanies({ force: true });
      const mapped = data.map((c) => ({
        id: String(c.id),
        name: c.name ?? "",
        sunucuKodu: c.sunucu_kodu ?? "",
        tc: c.tc ?? "",
        vergiNo: c.vergi_no ?? "",
        contract: contractOf(c.contract),
        contractRaw: c.contract ?? "",
        email: c.email ?? "",
        phone: c.phone ?? "",
      }));
      setRows(mapped);
      setPage(1);
    } catch (e) {
      console.error(e);
      setError("Sunucular yüklenemedi.");
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  /* ─────────── Arama ─────────── */
  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return rows;
    return rows.filter(
      (r) =>
        normalize(r.name).includes(q) || normalize(r.sunucuKodu).includes(q),
    );
  }, [rows, query]);

  /* ─────────── Sayfalama ─────────── */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  /* ─────────── Silme ─────────── */
  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" silinsin mi?`)) return;
    try {
      await adminService.deleteCompany(id);
      setSnack({ open: true, msg: "Sunucu silindi.", severity: "success" });
      await loadData(true);
    } catch (e) {
      console.error(e);
      setSnack({
        open: true,
        msg: "Silme başarısız oldu.",
        severity: "error",
      });
    }
  };

  /* ─────────── Render ─────────── */
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={800}
          color="#fff"
          sx={{ fontSize: 18 }}
        >
          Sunucu Listesi
        </Typography>
        <Typography
          sx={{
            ml: "auto",
            px: 1.25,
            py: 0.4,
            borderRadius: 5,
            backgroundColor: "rgba(255,255,255,0.14)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          {rows.length} kayıt
        </Typography>
        <Tooltip title="Yenile">
          <span>
            <IconButton
              size="small"
              onClick={() => loadData(true)}
              disabled={loading || refreshing}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.18)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
            >
              <RefreshOutlined
                sx={{
                  fontSize: 18,
                  animation:
                    loading || refreshing
                      ? "spin 1.2s linear infinite"
                      : "none",
                  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Arama */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2.5,
          backgroundColor: "#fff",
          boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
          flexShrink: 0,
          animation: `${fadeInUp} 0.45s ease-out 0.05s backwards`,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Sunucu kodu veya firma adı ara..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 18, color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              borderRadius: 2.5,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "rgba(255,255,255,0.98)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
              border: "1px solid rgba(255,255,255,0.30)",
              animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(15,50,65,0.20)",
                  borderRadius: 3,
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        background:
                          "linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%)",
                        color: "#334155",
                        fontWeight: 800,
                        fontSize: 10.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        py: 1,
                        px: 1.5,
                        borderBottom: "1px solid #CBD5E1",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell>Firma Adı</TableCell>
                    <TableCell>Sunucu Kodu</TableCell>
                    <TableCell>VKN/TCKN</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefon</TableCell>
                    <TableCell>Sözleşme</TableCell>
                    <TableCell align="right">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: textSecondary,
                          fontSize: 13,
                        }}
                      >
                        Kayıt bulunamadı
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((r, i) => (
                      <TableRow
                        key={r.id}
                        sx={{
                          backgroundColor: i % 2 ? "#F8FAFC" : "#fff",
                          "&:hover": { backgroundColor: "rgba(4,87,129,0.06)" },
                          transition: "background-color 0.2s ease",
                          "& td": {
                            py: 0.75,
                            px: 1.5,
                            fontSize: 12.5,
                            color: "#334155",
                            borderBottom: "1px solid #F1F5F9",
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 700, color: textPrimary }}>
                          {r.name}
                        </TableCell>
                        <TableCell>{r.sunucuKodu}</TableCell>
                        <TableCell>{r.tc || r.vergiNo || "-"}</TableCell>
                        <TableCell>{r.email || "-"}</TableCell>
                        <TableCell>{r.phone || "-"}</TableCell>
                        <TableCell>
                          <ContractChip contract={r.contract} />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            title="Düzenle"
                            onClick={() =>
                              setEditDialog({ open: true, company: r })
                            }
                            sx={{
                              color: "#045781",
                              "&:hover": {
                                backgroundColor: "rgba(4,87,129,0.10)",
                              },
                            }}
                          >
                            <EditOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Sil"
                            onClick={() => handleDelete(r.id, r.name)}
                            sx={{
                              color: "#B91C1C",
                              "&:hover": {
                                backgroundColor: "rgba(185,28,28,0.10)",
                              },
                            }}
                          >
                            <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* Sayfalama */}
          {filtered.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                px: 0.5,
                animation: `${fadeInUp} 0.4s ease-out 0.2s backwards`,
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                {Math.min((safePage - 1) * PAGE_SIZE + 1, filtered.length)}–
                {Math.min(safePage * PAGE_SIZE, filtered.length)} /{" "}
                {filtered.length} kayıt
              </Typography>
              <Pagination
                count={totalPages}
                page={safePage}
                onChange={(_, p) => setPage(p)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#fff",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255,255,255,0.24)",
                      fontWeight: 800,
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.14)",
                    },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Edit Dialog */}
      <CompanyEditDialog
        open={editDialog.open}
        company={editDialog.company}
        onClose={() => setEditDialog({ open: false, company: null })}
        onSaved={() => {
          setSnack({
            open: true,
            msg: "Sunucu güncellendi.",
            severity: "success",
          });
          loadData(true);
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ─────────── Sözleşme Rozeti ─────────── */
function ContractChip({ contract }) {
  const t = CONTRACT_CHIP[contract] || {
    fg: "#475569",
    bg: "#F1F5F9",
    border: "#CBD5E1",
  };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.9,
        py: 0.2,
        borderRadius: 1,
        backgroundColor: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        fontWeight: 700,
        fontSize: 10.5,
      }}
    >
      {contract}
    </Box>
  );
}
